/**
 * Container Lookup Step
 *
 * Phase 2 step for existing customers with containers on-site.
 * Allows customers to look up their container by phone or order number,
 * then select whether they want a swap or pickup.
 *
 * User Story:
 * "As an existing customer with a container on-site, I want to look up my
 * container by phone number or order number so I can request a swap or pickup"
 */

import { useState, useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { OrderLookup } from '../../../components/order/OrderLookup';
import { ContainerResult } from '../../../components/order/ContainerResult';
import { LoadingError } from '../../../components/order/LoadingError';
import { OrderDTO } from '../../../types/order';
import type { OrderForm } from '../../../schemas/order.schema';
import { useRateLimit } from '../../../hooks/useRateLimit';
import { ApiError } from '../../../services/api';

// Import the appropriate service based on environment
// In development, use mock service; in production, use real service
import { mockContainerService } from '../../../services/__mocks__/container.service.mock';
import { containerService as realContainerService } from '../../../services/container.service';

const USE_MOCK = import.meta.env.DEV;
const containerService = USE_MOCK ? mockContainerService : realContainerService;

export function ContainerLookupStep() {
  const { setValue, watch } = useFormContext<OrderForm>();

  // Local state for lookup
  const [lookupResult, setLookupResult] = useState<OrderDTO | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastSearchValue, setLastSearchValue] = useState<string>('');

  // Rate limiting (5 attempts per minute)
  const { canExecute, recordAttempt, getRateLimitInfo } = useRateLimit({
    maxRequests: 5,
    windowMs: 60000, // 1 minute
  });

  // Watch for selected action
  const selectedAction = watch('orderType') as 'swap' | 'pickup' | null;

  /**
   * Handle container lookup
   */
  const handleLookup = useCallback(async (searchValue: string) => {
    // Check rate limit
    if (!canExecute()) {
      const info = getRateLimitInfo();
      setError(
        `Too many lookup attempts. Please wait ${info.retryAfterSeconds} seconds before trying again.`
      );
      return;
    }

    // Record attempt
    recordAttempt();

    setIsLoading(true);
    setError(null);
    setLookupResult(null);
    setLastSearchValue(searchValue);

    try {
      // Call container service
      const result = await containerService.lookup({ searchValue });

      // Check if any containers were found
      if (result.containers.length === 0) {
        setError('No container found. Please check your phone number or order number.');
        setLookupResult(null);
        return;
      }

      // Use first container (in Phase 2, we expect only one)
      const container = result.containers[0];
      if (!container) {
        setError('No container data returned. Please try again.');
        setLookupResult(null);
        return;
      }

      setLookupResult(container);

      // Pre-fill form with found container details
      setValue('existingOrderId', container.orderId);
      setValue('street', container.serviceAddress.street);
      setValue('city', container.serviceAddress.city);
      setValue('state', container.serviceAddress.state);
      setValue('zipCode', container.serviceAddress.zipCode);
      setValue('containerSize', container.containerSize);
      setValue('materialType', container.materialType);

      // Clear error
      setError(null);
    } catch (err) {
      console.error('Container lookup error:', err);

      // Handle API errors with specific messages
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('An error occurred while looking up your container. Please try again.');
      }

      setLookupResult(null);
    } finally {
      setIsLoading(false);
    }
  }, [setValue, canExecute, recordAttempt, getRateLimitInfo]);

  /**
   * Handle retry - reuses the last search value
   */
  const handleRetry = useCallback(() => {
    if (lastSearchValue) {
      handleLookup(lastSearchValue);
    }
  }, [lastSearchValue, handleLookup]);

  /**
   * Handle action selection (swap or pickup)
   */
  const handleActionSelect = useCallback((action: 'swap' | 'pickup') => {
    setValue('orderType', action, {
      shouldValidate: true,
      shouldDirty: true,
    });
  }, [setValue]);

  return (
    <div className="space-y-6">
      {/* Lookup Form */}
      <OrderLookup
        onLookup={handleLookup}
        isLoading={isLoading}
        error={error}
      />

      {/* Error Display with Retry */}
      {error && (
        <LoadingError
          error={error}
          onRetry={handleRetry}
        />
      )}

      {/* Results (only shown if order found) */}
      {lookupResult && (
        <ContainerResult
          order={lookupResult}
          selectedAction={selectedAction as 'swap' | 'pickup' | null}
          onActionSelect={handleActionSelect}
        />
      )}

      {/* Helper Text */}
      {!lookupResult && !error && !isLoading && (
        <div className="text-center py-8">
          <p className="text-sm text-gray-500">
            Enter your phone number or order number above to get started
          </p>
        </div>
      )}
    </div>
  );
}
